import { useConversationPalette } from './conversation/ConversationTheme';
import React, { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { SuccessIcon } from '@/components/icons';
import { captureException } from '../lib/telemetry/posthog/frontend';

/** Props for the SuccessScreen component. */
interface SuccessScreenProps {
  /** Whether to show the holito referral CTA. Defaults to true. */
  showHolitoReferral?: boolean;
  /** Campaign-configured completion behaviour. Defaults to 'message'. */
  completionAction?: 'message' | 'redirect';
  /** Custom success body. Falls back to default copy when null/blank. */
  completionMessage?: string | null;
  /** Destination URL when completionAction is 'redirect'. */
  completionRedirectUrl?: string | null;
  /**
   * When set, the redirect is dispatched to the parent window via
   * postMessage instead of `window.location.href`. The snippet
   * library on the parent is responsible for performing the actual
   * navigation. Required for embed flows where the iframe cannot
   * navigate the top window directly.
   */
  embedOrigin?: string | null;
}

const DEFAULT_BODY = "Your response has been submitted. Thank you for your time.";

/** True when value is a non-blank http(s) URL. */
function isSafeRedirectUrl(value: string | null | undefined): value is string {
  if (!value || !value.trim()) return false;
  try {
    const u = new URL(value);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
}

/**
 * Success screen shown after completing an interview.
 * Renders inside the App layout (header + LiquidEtherBackground are provided by the parent).
 * Uses the same animated-border / glass-card pattern as PreInterviewForm.
 * Confetti fires on a dedicated full-screen canvas behind the dialog.
 *
 * When the campaign is configured for completion_action='redirect' and
 * a valid URL is set, the screen dispatches the navigation on mount:
 * direct flows navigate the current window, embed flows postMessage
 * the parent so the snippet library can perform the redirect.
 */
const SuccessScreen: React.FC<SuccessScreenProps> = ({
  showHolitoReferral = true,
  completionAction = 'message',
  completionMessage = null,
  completionRedirectUrl = null,
  embedOrigin = null,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const palette = useConversationPalette();

  const shouldRedirect =
    completionAction === 'redirect' && isSafeRedirectUrl(completionRedirectUrl);
  const trimmedMessage = completionMessage?.trim() ?? '';
  const bodyCopy = trimmedMessage.length > 0 ? trimmedMessage : DEFAULT_BODY;

  useEffect(() => {
    if (!shouldRedirect || !completionRedirectUrl) return;
    if (embedOrigin) {
      try {
        window.parent.postMessage(
          { type: 'hollie:complete_redirect', url: completionRedirectUrl },
          embedOrigin,
        );
      } catch (err) {
        captureException(
          err instanceof Error ? err : new Error(String(err)),
          'SuccessScreen.parentRedirect',
          { embedOrigin },
        );
      }
      return;
    }
    window.location.href = completionRedirectUrl;
  }, [shouldRedirect, completionRedirectUrl, embedOrigin]);

  useEffect(() => {
    if (shouldRedirect) return;
    if (!canvasRef.current) return;

    try {
      const fire = confetti.create(canvasRef.current, { resize: true });
      void fire({
        particleCount: 120,
        spread: 70,
        origin: { y: 0.6 },
        colors: palette ? [palette.accent, palette.soft, '#ffffff'] : ['#7c3aed', '#a78bfa', '#ffffff'],
      });
    } catch (err) {
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'SuccessScreen.confetti',
      );
    }
  }, [shouldRedirect, palette]);

  return (
    <>
      {/* Full-screen confetti canvas behind the dialog */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0 w-screen h-screen pointer-events-none"
        style={{ zIndex: 5 }}
      />

      <div className="flex flex-col items-center justify-center grow text-center px-4 py-8 md:p-8 max-w-lg w-full mx-auto animate-fade-in relative" style={{ zIndex: 10 }}>

      <div className="animated-border-wrapper w-full">
        <div className="glass-card p-8 md:p-10 flex flex-col items-center">

          {/* Check icon with bounce-in entrance */}
          <SuccessIcon
            className={`w-20 h-20 mb-8 ${palette ? 'text-accent-soft' : 'text-success'}`}
            style={{ animation: 'bounce-in 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) forwards' }}
          />

          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            You're all done!
          </h1>

          <p className="text-base text-muted-foreground leading-relaxed mb-8 whitespace-pre-line">
            {bodyCopy}
          </p>

          {!embedOrigin && showHolitoReferral && (
            <>
              <div className="w-full h-px bg-surface-raised/50 mb-6" />

              <div className="w-full bg-wash-accent rounded-xl p-5">
                <p className="text-base text-muted-foreground">
                  Want to create your own?{' '}
                  <a
                    href="/login"
                    className="text-accent-soft font-semibold hover:underline"
                  >
                    Try holito free
                  </a>
                </p>
              </div>
            </>
          )}

        </div>
      </div>

    </div>
    </>
  );
};

export default SuccessScreen;
